import { ClientTrendFilter, ProductTrendFilter } from "@acto/gql/graphql";

// Quick-filter model for the analysis product list. The empty state is "Alle".
// The dropdown is single-select (one checkmark), and the two trend directions
// are mutually exclusive: growth AND decline combine as AND server-side (product
// decision 2026-07-06) and so always yield an empty list, leaving no reason to
// select both. Picking a direction therefore switches to it rather than adding
// to the other. Mirrors pages/quickFilter.ts for customers, minus the focus
// facet (no product analog).
export interface ProductQuickFilterState {
  growth: boolean;
  decline: boolean;
}

export const EMPTY_PRODUCT_QUICK_FILTER: ProductQuickFilterState = { growth: false, decline: false };

export function isProductQuickFilterEmpty(state: ProductQuickFilterState): boolean {
  return !state.growth && !state.decline;
}

export function toggleProductQuickFilterKey(state: ProductQuickFilterState, key: string): ProductQuickFilterState {
  // Mutually exclusive directions: selecting a direction switches to it (clearing
  // the other), and re-selecting the active direction clears back to "Alle".
  if (key === "growth") {
    return state.growth ? EMPTY_PRODUCT_QUICK_FILTER : { growth: true, decline: false };
  }
  if (key === "decline") {
    return state.decline ? EMPTY_PRODUCT_QUICK_FILTER : { growth: false, decline: true };
  }
  return state;
}

export function toTrendsVariables(state: ProductQuickFilterState): ProductTrendFilter[] | undefined {
  const trends: ProductTrendFilter[] = [];
  if (state.growth) trends.push(ProductTrendFilter.Growth);
  if (state.decline) trends.push(ProductTrendFilter.Decline);
  return trends.length > 0 ? trends : undefined;
}

export function toClientTrendsVariables(trends: ProductTrendFilter[] | undefined): ClientTrendFilter[] | undefined {
  if (!trends?.length) return undefined;
  return trends.map((trend) =>
    trend === ProductTrendFilter.Growth ? ClientTrendFilter.Growth : ClientTrendFilter.Decline,
  );
}

/** The query-shaped trend selection → the ambient-context token (directions are single-select). */
export function trendsToTrendFilter(trends: ProductTrendFilter[] | undefined): "growth" | "decline" | undefined {
  if (!trends || trends.length !== 1) return undefined;
  return trends[0] === ProductTrendFilter.Growth ? "growth" : "decline";
}
